import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, Subject, throwError, firstValueFrom, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Socket } from 'ngx-socket-io';

import { environment } from '../../environments/environment';
import { LOGIN_GOTO_QUERY_PARAM, USER_ROLES } from '../app.constants';
import { persistGotoForLogin, resolvePostLoginUrl, clearLegacyGoto } from 'app/_utils/helpers';
import { ProfileService } from './profile.service';
import { CompanyService } from './company.service';

@Injectable()
export class AuthenticationService implements OnDestroy {
  private currentUser: any;
  private handleLogin = (user) => {
    if (user.role === USER_ROLES.CONTACT) {
      return false;
    }
    this.companyService.clearCache();
    this.currentUser = user;
    this.saveToLocalStorage();
    localStorage.setItem('QRCODE_PLACEHOLDER_URI', `${environment.apiBaseUrl}/event_qrcode?token=`)
    this.socket.connect();
    return true;
  }

  private readonly onStorageCrossTab = (event: StorageEvent) => {
    if (event.key !== 'currentUser') {
      return;
    }
    this.ngZone.run(() => {
      if (event.newValue) {
        try {
          const user = JSON.parse(event.newValue);
          if (user.role === USER_ROLES.CONTACT) {
            return;
          }
          this.applyCrossTabLogin(user);
        } catch {
          // ignore invalid JSON
        }
      } else {
        this.applyCrossTabLogout();
      }
    });
  };

  constructor(
    private http: HttpClient,
    private httpBackend: HttpBackend,
    private socket: Socket,
    private router: Router,
    private ngZone: NgZone,
    private profileService: ProfileService,
    private companyService: CompanyService,
  ) {
    const user = localStorage.getItem('currentUser');
    if (user) {
      try {
        this.currentUser = JSON.parse(user);
        if (!this.isSessionValid()) {
          this.purgeLocalSession();
        }
      } catch {
        this.purgeLocalSession();
      }
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', this.onStorageCrossTab);
    }
  }

  /** Confirm a restored local JWT is accepted by the API before the app routes. */
  ensureServerSessionValid(): Promise<void> {
    if (!this.isSessionValid()) {
      return Promise.resolve();
    }
    const token = this.token();
    const bareHttp = new HttpClient(this.httpBackend);
    return firstValueFrom(
      bareHttp.get(`${environment.apiBaseUrl}/profile`, {
        headers: { Authorization: token },
      }).pipe(
        catchError(() => {
          this.purgeLocalSession();
          return of(null);
        }),
      ),
    ).then(() => undefined);
  }

  private applyCrossTabLogin(user: any): void {
    this.currentUser = user;
    localStorage.setItem(
      'QRCODE_PLACEHOLDER_URI',
      `${environment.apiBaseUrl}/event_qrcode?token=`
    );
    this.companyService.clearCache();
    this.profileService.resetLoaded();
    this.profileService.getProfile().subscribe({
      next: () => {
        this.socket.connect();
        if (this.isPublicAuthRoute()) {
          this.router.navigateByUrl(this.getPostLoginUrl());
        }
      },
      error: () => this.purgeLocalSession(),
    });
  }

  private applyCrossTabLogout(): void {
    this.currentUser = null;
    this.companyService.clearCache();
    this.profileService.resetLoaded();
    this.socket.disconnect();
    if (!this.isPublicAuthRoute()) {
      this.router.navigate(['login'], {
        queryParams: persistGotoForLogin(this.router.url),
      });
    }
  }

  /** Clear client session without calling the sign-out API. */
  purgeLocalSession(): void {
    this.currentUser = null;
    localStorage.removeItem('currentUser');
    localStorage.removeItem('QRCODE_PLACEHOLDER_URI');
    localStorage.removeItem('chatbot_state');
    clearLegacyGoto();
    this.companyService.clearCache();
    this.profileService.resetLoaded();
    this.socket.disconnect();
  }

  ngOnDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', this.onStorageCrossTab);
    }
  }

  /** Same redirect target as LoginComponent after sign-in (`?goto=` query param only). */
  private getPostLoginUrl(): string {
    const tree = this.router.parseUrl(this.router.url);
    return resolvePostLoginUrl(tree.queryParams[LOGIN_GOTO_QUERY_PARAM]);
  }

  private isPublicAuthRoute(): boolean {
    const path = this.router.url.split('?')[0];
    return ['/login', '/forgot-password', '/reset-password'].includes(path);
  }

  login(model): Observable<boolean> {
    return this.http.post(`${environment.apiBaseUrl}/auth/signin`, model)
      .pipe(
        map(this.handleLogin),
        catchError(res => throwError(res))
      );
  }

  loginWithGoogle(model): Observable<boolean> {
    return this.http.post(`${environment.apiBaseUrl}/auth/signin_with_google`, model)
      .pipe(
        map(this.handleLogin),
        catchError(res => throwError(res))
      );
  }

  loginWithFacebook(model): Observable<boolean> {
    return this.http.post(`${environment.apiBaseUrl}/auth/signin_with_facebook`, model)
      .pipe(
        map(this.handleLogin),
        catchError(res => throwError(res))
      );
  }

  loginWithApple(model): Observable<boolean> {
    return this.http.post(`${environment.apiBaseUrl}/auth/signin_with_apple`, model)
      .pipe(
        map(this.handleLogin),
        catchError(res => throwError(res))
      );
  }

  loginWithAuth0(model): Observable<boolean> {
    return this.http.post(`${environment.apiBaseUrl}/auth/signin_with_auth0`, model)
      .pipe(
        map(this.handleLogin),
        catchError(res => throwError(res))
      );
  }

  forgotPassword(username) {
    return this.http.post(`${environment.apiBaseUrl}/auth/forgot_password`, {
      username
    });
  }

  forgotUsername(email) {
    return this.http.post(`${environment.apiBaseUrl}/auth/forgot_username`, {
      email
    });
  }

  resetPassword(code, password) {
    return this.http.post(`${environment.apiBaseUrl}/auth/reset_password`, {
      code,
      password
    });
  }

  /** True when a non-expired token is present in memory. */
  isSessionValid(): boolean {
    if (!this.token() || !this.id()) {
      return false;
    }
    const expMs = this.tokenExpiresAtMs();
    return !expMs || expMs > Date.now();
  }

  token(): string {
    return this.currentUser && this.currentUser.token;
  }

  /** Returns JWT expiry time in ms since epoch, or null if not parseable. */
  tokenExpiresAtMs(): number | null {
    const t = this.token();
    if (!t) return null;
    try {
      const raw = t.replace(/^Bearer\s+/i, '');
      const parts = raw.split('.');
      if (parts.length < 2) return null;
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      if (!payload.exp) return null;
      return payload.exp * 1000;
    } catch {
      return null;
    }
  }

  /** Calls backend refresh endpoint and updates the stored token. */
  refreshToken(): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/auth/refresh_token`, {}).pipe(
      map((res: any) => {
        const newToken = res && (res.data && res.data.token || res.token);
        if (newToken && this.currentUser) {
          this.currentUser.token = newToken;
          this.saveToLocalStorage();
        }
        return res;
      })
    );
  }

  role(): string {
    return this.currentUser && this.currentUser.role;
  }

  id(): string {
    return this.currentUser && this.currentUser.id;
  }

  staff_id(): string {
    return this.currentUser && this.currentUser.staff_id;
  }

  staff_agency_id(): string {
    return this.currentUser && this.currentUser.agency_id;
  }

  client_id(): number {
    return this.currentUser && this.currentUser.client_id;
  }

  client_company_id(): string {
    return this.currentUser && this.currentUser.company_id;
  }

  contact_id(): string {
    return this.currentUser && this.currentUser.contact_id;
  }

  client_permissions(): any[] {
    if (this.currentUser && this.currentUser.client_id) {
      const permissions = this.currentUser.permissions.map((permission) => permission.id);
      return permissions;
    }
    return [];
  }
  client_level(): any {
    if (this.currentUser && this.currentUser.client_id) {
      return this.currentUser.level;
    }
    return {};
  }

  current_company_id(): string {
    return this.currentUser && this.currentUser.current_company_id;
  }

  current_bureau_id(): string {
    return this.currentUser && this.currentUser.current_bureau_id;
  }

  current_company_name(): string {
    return this.currentUser && this.currentUser.current_company_name;
  }

  current_bureau_name(): string {
    return this.currentUser && this.currentUser.current_bureau_name;
  }

  timezone_id(): string {
    return (this.currentUser && this.currentUser.timezone_id) || 'America/New_York';
  }

  /**
   * Fires whenever the current company/bureau selection changes (first pick
   * after sign-in, header switch). The sandbox banner listens for this.
   */
  readonly currentCompanyChanged$ = new Subject<string>();

  setCurrentBureau(company_id: string, company_name: string, bureau_id: string, bureau_name: string) {
    if (this.currentUser) {
      const changed = this.currentUser.current_company_id !== company_id;
      this.currentUser.current_company_id = company_id;
      this.currentUser.current_company_name = company_name;
      this.currentUser.current_bureau_id = bureau_id;
      this.currentUser.current_bureau_name = bureau_name;
      this.saveToLocalStorage();
      if (changed) this.currentCompanyChanged$.next(company_id);
    }
  }

  saveToLocalStorage() {
    localStorage.setItem('currentUser', JSON.stringify(this.currentUser));
  }

  is_staff(): boolean {
    return this.role() === USER_ROLES.STAFF;
  }

  is_agency_staff(): boolean {
    return this.role() === USER_ROLES.AGENCY_STAFF;
  }

  is_client(): boolean {
    return this.role() === USER_ROLES.CLIENT;
  }

  is_contact(): boolean {
    return this.role() === USER_ROLES.CONTACT;
  }

  is_staff_or_agency_staff(): boolean {
    return this.is_staff() || this.is_agency_staff();
  }

  is_client_corporate(): boolean {
    return this.is_client() && this.client_level().is_corporate;
  }

  is_staff_can_account(): boolean {
    return this.is_staff() && this.currentUser.can_account;
  }

  is_agency_staff_can_account(): boolean {
    return this.is_agency_staff() && this.currentUser.can_account;
  }

  is_staff_super(): boolean {
    return this.is_staff() && this.currentUser.is_super;
  }

  is_agency_staff_super(): boolean {
    return this.is_agency_staff() && this.currentUser.is_super;
  }

  is_super(): boolean {
    if (!this.currentUser) {
      return false;
    }
    return (this.is_staff() || this.is_agency_staff()) && this.currentUser.is_super === true;
  }

  can_account(): boolean {
    return this.is_staff_can_account() || this.is_agency_staff_can_account();
  }

  is_in(roles): boolean {
    return roles.includes(this.role());
  }

  logout(storageOnly = false): void {
    if (!storageOnly) {
      this.http.post(`${environment.apiBaseUrl}/auth/signout`, {}, {
        headers: {
          'Authorization': this.token(),
        }
      }).subscribe();
    }
    this.purgeLocalSession();
  }

  getCurrentUser(): any {
    return this.currentUser;
  }
}
